import React, { useState, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import { useAuth } from '../context/AuthContext';
import { getSwalTheme, getSwalConfirmTheme } from '../utils/swalTheme';
import api from '../services/api';
import PasswordInput from '../components/PasswordInput';
import PostListItem from '../components/PostListItem';
import EditShiftModal from '../components/EditShiftModal';
import EditDriverPostModal from '../components/EditDriverPostModal';
import ShiftDetailsModal from '../components/ShiftDetailsModal';
import DriverPostProfileModal from '../components/DriverPostProfileModal';
import { formatPhone, normalizePhoneInput } from '../utils/formatters';
import './AccountPage.css';

const AccountPage = () => {
  const { user, updateUser } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: ''
  });

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // UI state
  const [activeSection, setActiveSection] = useState('profile');
  const [loading, setSaving] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // My Posts state
  const [myPosts, setMyPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [editModalShift, setEditModalShift] = useState(null);
  const [editModalDriverPost, setEditModalDriverPost] = useState(null);
  const [detailsModalShift, setDetailsModalShift] = useState(null);
  const [driverProfilePost, setDriverProfilePost] = useState(null);

  // Initialize form data from user
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        phone: user.phone ? formatPhone(user.phone) : ''
      });
    }
  }, [user]);

  // Format member since date
  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : null;

  // Get user initials for avatar
  const getInitials = () => {
    if (!user?.name) return '?';
    const names = user.name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return names[0][0].toUpperCase();
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    const formatted = name === 'phone' ? formatPhone(value) : value;
    setFormData(prev => ({ ...prev, [name]: formatted }));
    setHasChanges(true);

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Handle password field changes
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));

    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    const phoneDigits = normalizePhoneInput(formData.phone);
    if (!phoneDigits) {
      errors.phone = 'Phone number is required';
    } else if (phoneDigits.length !== 10) {
      errors.phone = 'Phone number must be 10 digits';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validate password form
  const validatePasswordForm = () => {
    const errors = {};

    if (!passwordData.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }

    if (!passwordData.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setFieldErrors(prev => ({ ...prev, ...errors }));
    return Object.keys(errors).length === 0;
  };

  // Handle profile update
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSaving(true);

    try {
      const response = await api.put('/auth/me', {
        ...formData,
        phone: normalizePhoneInput(formData.phone)
      });

      updateUser(response.data.user);
      setHasChanges(false);

      Swal.fire({
        ...getSwalTheme(),
        icon: 'success',
        title: 'Profile Updated',
        text: 'Your changes have been saved.',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (err) {
      Swal.fire({
        ...getSwalTheme(),
        icon: 'error',
        title: 'Update Failed',
        text: err.response?.data?.error || 'Failed to update profile.'
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle password change
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!validatePasswordForm()) return;

    setPasswordLoading(true);

    try {
      await api.put('/auth/me/password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordSection(false);
      setFieldErrors({});

      Swal.fire({
        ...getSwalTheme(),
        icon: 'success',
        title: 'Password Updated',
        text: 'Your password has been changed successfully.',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (err) {
      Swal.fire({
        ...getSwalTheme(),
        icon: 'error',
        title: 'Update Failed',
        text: err.response?.data?.error || 'Failed to change password.'
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  // Cancel password change
  const cancelPasswordChange = () => {
    setShowPasswordSection(false);
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setFieldErrors(prev => {
      const { currentPassword, newPassword, confirmPassword, ...rest } = prev;
      return rest;
    });
  };

  // Fetch my posts
  const fetchMyPosts = useCallback(async () => {
    setPostsLoading(true);
    try {
      const response = await api.get('/posts?my_posts=true&include_expired=true');
      setMyPosts(response.data.posts || []);
    } catch (err) {
      console.error('Failed to fetch my posts:', err);
    } finally {
      setPostsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeSection === 'my-posts') {
      fetchMyPosts();
    }
  }, [activeSection, fetchMyPosts]);

  // My Posts handlers
  const handlePostRenew = async (postId, postType) => {
    const isOwnerPost = postType === 'owner' || postType === 'shift';
    try {
      if (isOwnerPost) {
        await api.post(`/shifts/${postId}/renew`);
      } else {
        await api.post(`/driver-posts/${postId}/renew`);
      }
      fetchMyPosts();
      Swal.fire({
        ...getSwalTheme(),
        icon: 'success',
        title: 'Renewed!',
        text: 'Your post has been renewed for 30 more days.',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (err) {
      Swal.fire({
        ...getSwalTheme(),
        icon: 'error',
        title: 'Renew Failed',
        text: err.response?.data?.error || 'Failed to renew post.'
      });
    }
  };

  const handlePostEdit = (post) => {
    if (post.post_type === 'driver') {
      setEditModalDriverPost(post);
    } else {
      setEditModalShift(post);
    }
  };

  const handlePostDelete = async (postId, postType) => {
    const isOwnerPost = postType === 'owner' || postType === 'shift';
    const result = await Swal.fire({
      ...getSwalConfirmTheme(),
      title: isOwnerPost ? 'Delete Car Listing?' : 'Delete Driver Listing?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      confirmButtonText: 'Yes, delete it'
    });

    if (!result.isConfirmed) return;

    try {
      if (isOwnerPost) {
        await api.delete(`/shifts/${postId}`);
      } else {
        await api.delete(`/driver-posts/${postId}`);
      }
      fetchMyPosts();
      Swal.fire({
        ...getSwalTheme(),
        icon: 'success',
        title: 'Deleted!',
        text: 'Your post has been deleted.'
      });
    } catch (err) {
      Swal.fire({
        ...getSwalTheme(),
        icon: 'error',
        title: 'Delete Failed',
        text: err.response?.data?.error || 'Failed to delete.'
      });
    }
  };

  const handleViewDetails = (shift) => setDetailsModalShift(shift);
  const handleViewDriverProfile = (post) => setDriverProfilePost(post);

  return (
    <div className="account-page">
      <div className="account-layout">
        {/* Sidebar with profile summary */}
        <aside className="account-sidebar">
          <div className="profile-card">
            <div className="profile-avatar">
              {getInitials()}
            </div>
            <h2 className="profile-name">{user?.name}</h2>
            <span className={`profile-role ${user?.role}`}>
              {user?.role === 'admin' ? 'Admin' : 'User'}
            </span>
            {memberSince && (
              <p className="profile-member-since">Member since {memberSince}</p>
            )}
          </div>

          <nav className="account-nav">
            <button
              type="button"
              className={`nav-item ${activeSection === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveSection('profile')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              Profile
            </button>
            <button
              type="button"
              className={`nav-item ${activeSection === 'security' ? 'active' : ''}`}
              onClick={() => setActiveSection('security')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              Security
            </button>
            {user?.role !== 'admin' && (
            <button
              type="button"
              className={`nav-item ${activeSection === 'my-posts' ? 'active' : ''}`}
              onClick={() => setActiveSection('my-posts')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
              My Posts
            </button>
            )}
          </nav>
        </aside>

        {/* Main content */}
        <main className="account-main">
          <form onSubmit={handleSubmit}>
            {/* Profile Section */}
            {activeSection === 'profile' && (
            <section id="profile" className="settings-section">
              <div className="section-header">
                <h2>Profile</h2>
                <p>Manage your account details</p>
              </div>

              <div className="form-card">
                <div className="form-group">
                  <label htmlFor="name">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    className={fieldErrors.name ? 'error' : ''}
                  />
                  {fieldErrors.name && <span className="field-error">{fieldErrors.name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <div className="input-with-badge">
                    <input
                      type="email"
                      id="email"
                      value={user?.email || ''}
                      disabled
                      className="disabled"
                    />
                    <span className="input-badge">Cannot be changed</span>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(212) 555-1234"
                    className={fieldErrors.phone ? 'error' : ''}
                  />
                  {fieldErrors.phone && <span className="field-error">{fieldErrors.phone}</span>}
                </div>

              </div>
            </section>
            )}

            {/* Security Section */}
            {activeSection === 'security' && (
            <section id="security" className="settings-section">
              <div className="section-header">
                <h2>Security</h2>
                <p>Manage your password and security settings</p>
              </div>

              <div className="form-card">
                {!showPasswordSection ? (
                  <div className="security-overview">
                    <div className="security-item">
                      <div className="security-info">
                        <h4>Password</h4>
                        <p>Last changed: Unknown</p>
                      </div>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => setShowPasswordSection(true)}
                      >
                        Change Password
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="password-form">
                    <div className="form-group">
                      <label htmlFor="currentPassword">Current Password</label>
                      <PasswordInput
                        id="currentPassword"
                        name="currentPassword"
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        placeholder="Enter current password"
                        autoComplete="current-password"
                        className={fieldErrors.currentPassword ? 'error' : ''}
                      />
                      {fieldErrors.currentPassword && (
                        <span className="field-error">{fieldErrors.currentPassword}</span>
                      )}
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="newPassword">New Password</label>
                        <PasswordInput
                          id="newPassword"
                          name="newPassword"
                          value={passwordData.newPassword}
                          onChange={handlePasswordChange}
                          placeholder="Min 8 characters"
                          autoComplete="new-password"
                          className={fieldErrors.newPassword ? 'error' : ''}
                        />
                        {fieldErrors.newPassword && (
                          <span className="field-error">{fieldErrors.newPassword}</span>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="confirmPassword">Confirm Password</label>
                        <PasswordInput
                          id="confirmPassword"
                          name="confirmPassword"
                          value={passwordData.confirmPassword}
                          onChange={handlePasswordChange}
                          placeholder="Confirm new password"
                          autoComplete="new-password"
                          className={fieldErrors.confirmPassword ? 'error' : ''}
                        />
                        {fieldErrors.confirmPassword && (
                          <span className="field-error">{fieldErrors.confirmPassword}</span>
                        )}
                      </div>
                    </div>

                    <div className="password-actions">
                      <button
                        type="button"
                        className="btn-text"
                        onClick={cancelPasswordChange}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={handlePasswordSubmit}
                        disabled={passwordLoading}
                      >
                        {passwordLoading ? (
                          <>
                            <span className="spinner"></span>
                            Updating...
                          </>
                        ) : (
                          'Update Password'
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </section>
            )}

            {/* Save Button - only show on profile tab */}
            {activeSection === 'profile' && (
            <div className="form-footer">
              <div className="save-indicator">
                {hasChanges && (
                  <span className="unsaved-badge">Unsaved changes</span>
                )}
              </div>
              <button
                type="submit"
                className="btn-primary btn-save"
                disabled={loading || !hasChanges}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
            )}
          </form>

          {/* My Posts Section */}
          {activeSection === 'my-posts' && (
            <section id="my-posts" className="settings-section">
              <div className="section-header">
                <h2>My Posts</h2>
                <p>View and manage all your listings</p>
              </div>

              {postsLoading ? (
                <div className="my-posts-loading">
                  <span className="spinner"></span>
                  <p>Loading your posts...</p>
                </div>
              ) : myPosts.length === 0 ? (
                <div className="my-posts-empty">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  <p>You haven't created any posts yet.</p>
                </div>
              ) : (
                <div className="my-posts-list">
                  {myPosts.map((post) => (
                    <PostListItem
                      key={`${post.post_type}-${post.id}`}
                      post={post}
                      userRole={user?.role}
                      userId={user?.id}
                      onViewDetails={handleViewDetails}
                      onViewProfile={handleViewDriverProfile}
                      onEdit={handlePostEdit}
                      onDelete={handlePostDelete}
                      onRenew={handlePostRenew}
                    />
                  ))}
                </div>
              )}
            </section>
          )}
        </main>
      </div>

      {editModalShift && (
        <EditShiftModal
          shift={editModalShift}
          onClose={() => setEditModalShift(null)}
          onUpdate={fetchMyPosts}
        />
      )}

      {editModalDriverPost && (
        <EditDriverPostModal
          post={editModalDriverPost}
          onClose={() => setEditModalDriverPost(null)}
          onUpdate={fetchMyPosts}
        />
      )}

      {detailsModalShift && (
        <ShiftDetailsModal
          shift={detailsModalShift}
          onClose={() => setDetailsModalShift(null)}
          userRole={user?.role}
        />
      )}

      {driverProfilePost && (
        <DriverPostProfileModal
          post={driverProfilePost}
          onClose={() => setDriverProfilePost(null)}
        />
      )}
    </div>
  );
};

export default AccountPage;
